/**
 * Pure presentation for entity timelines (`GET /admin/v1/timeline`): what each
 * rule decided, pending item labels, why an item closed, and the merged,
 * chronological list of events, messages, and closed pending items.
 *
 * Decisions are stored as JSON by the server (`policies/apply.rs`), so every
 * parser here is defensive: unknown shapes fall back to neutral text.
 */
import type { EntityEvent, MessageSummary, PolicyItem, Timeline } from '@/api/types'

// ------------------------------------------------------------------ keys

/** `name=value` split, or just a value. */
export function parseKeyQuery(raw: string): { name?: string; value: string } | undefined {
  const s = raw.trim()
  if (!s) return undefined
  const i = s.indexOf('=')
  if (i > 0 && i < s.length - 1) return { name: s.slice(0, i).trim(), value: s.slice(i + 1).trim() }
  return { value: s }
}

/**
 * Whether typed palette text looks like a key value worth a "Find timeline"
 * action: one token, at least 3 characters, with a digit (an order number,
 * say), or an explicit `name=value`.
 */
export function looksLikeKey(raw: string): boolean {
  const s = raw.trim()
  if (s.length < 3 || /\s/.test(s)) return false
  if (/^[A-Za-z_][\w.-]*=\S+$/.test(s)) return true
  return /\d/.test(s) && /^[\w.:/#-]+$/.test(s)
}

/** `["order=WS1", "credit=CR2"]` as name/value pairs. */
export function splitEntityKeys(keys: readonly string[] | null | undefined) {
  return (keys ?? []).flatMap((k) => {
    const i = k.indexOf('=')
    return i > 0 ? [{ name: k.slice(0, i), value: k.slice(i + 1), raw: k }] : []
  })
}

// ------------------------------------------------------------------ time

/**
 * A compact absolute time: "14:00" today, "Oct 24, 14:00" this year, with the
 * year otherwise. `now` is a parameter for tests.
 */
export function formatWhen(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  if (d.toDateString() === now.toDateString()) return time
  const date = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  })
  return `${date}, ${time}`
}

// ------------------------------------------------------------- decisions

export type DecisionAction = 'send' | 'hold' | 'wait' | 'collect' | 'skip'

export interface RuleDecision {
  rule_id: string
  rule_name?: string | null
  action: DecisionAction | string
  key_name?: string
  key_value?: string
  /** hold */
  until?: string
  /** wait */
  deadline?: string
  hold_until?: string | null
  /** collect: the open window it joined, if any */
  join?: string | null
  release_at?: string
  /** skip */
  reason?: string
  code?: string
  detail?: string
  capture?: boolean
}

export interface CancelledItem {
  item_id: string
  rule?: string | null
  message_id?: string | null
}

export interface EventDecisions {
  rules: RuleDecision[]
  cancelled: CancelledItem[]
  satisfied: string[]
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)

/** `entity_events.decisions` as typed lists; anything malformed is dropped. */
export function parseDecisions(raw: unknown): EventDecisions {
  const d = isObj(raw) ? raw : {}
  const rules = Array.isArray(d.rules)
    ? d.rules.filter((r): r is RuleDecision => isObj(r) && typeof r.action === 'string')
    : []
  const cancelled = Array.isArray(d.cancelled)
    ? d.cancelled.filter((c): c is CancelledItem => isObj(c) && typeof c.item_id === 'string')
    : []
  const satisfied = Array.isArray(d.satisfied)
    ? d.satisfied.filter((s): s is string => typeof s === 'string')
    : []
  return { rules, cancelled, satisfied }
}

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'brass' | 'held'

export interface Label {
  label: string
  tone: Tone
  /** A second line, such as a skip reason or what happens on timeout. */
  detail?: string
}

const sentence = (s: string) => s.replace(/^\w/, (c) => c.toUpperCase())

/** What one rule decided for an event, in plain words. */
export function decisionLabel(
  d: RuleDecision,
  opts: { item?: PolicyItem; now?: Date } = {},
): Label {
  const when = (iso: string | null | undefined) => formatWhen(iso, opts.now)
  switch (d.action) {
    case 'send':
      return { label: 'Sent', tone: 'success' }
    case 'hold':
      return { label: `Held until ${when(d.until)}`, tone: 'held' }
    case 'wait': {
      const wait = opts.item?.wait
      return {
        label: wait
          ? `Waiting for ${wait.event} (${wait.until}) until ${when(d.deadline)}`
          : `Waiting for a prerequisite until ${when(d.deadline)}`,
        tone: 'held',
        detail: wait ? onTimeoutText(wait.on_timeout) : undefined,
      }
    }
    case 'collect':
      return {
        label: d.join
          ? `Added to the open collection, due ${when(d.release_at)}`
          : `Collecting until ${when(d.release_at)}`,
        tone: 'held',
      }
    case 'skip':
      return {
        label: 'Skipped',
        tone: 'neutral',
        detail: d.detail ? sentence(d.detail) : d.code ? skipCodeText(d.code) : undefined,
      }
    default:
      return { label: sentence(String(d.action)), tone: 'neutral' }
  }
}

function skipCodeText(code: string): string {
  switch (code) {
    case 'missing_key':
      return 'The event has no value for the key this rule’s policy works on'
    case 'newer_event_seen':
      return 'A later event was already received for this key'
    case 'suppressed_by_event':
      return 'A suppressing event was received for this key within the window'
    case 'duplicate':
      return 'A message with the same dedupe key already exists'
    default:
      return code
  }
}

function onTimeoutText(onTimeout: string): string {
  return onTimeout === 'drop'
    ? 'If it doesn’t arrive, the email is dropped.'
    : 'If it doesn’t arrive, the email sends anyway.'
}

// --------------------------------------------------------- pending items

/**
 * How many events a collect item holds, from the history: the decisions that
 * joined it by ID, plus the one that opened it (a collect decision for the
 * same rule and key with no `join`, recorded when the item opened). The item
 * itself doesn't carry a count.
 */
export function collectedCount(item: PolicyItem, events: readonly EntityEvent[]): number {
  if (item.kind !== 'collect') return 0
  let joined = 0
  let opened = false
  const openedAt = Date.parse(item.opened_at)
  for (const e of events) {
    if (e.test !== item.test) continue
    for (const r of parseDecisions(e.decisions).rules) {
      if (r.action !== 'collect' || r.rule_id !== item.rule_id) continue
      if (r.key_value !== undefined && r.key_value !== item.key_value) continue
      if (r.join === item.id) joined++
      else if (!r.join && Math.abs(Date.parse(e.received_at) - openedAt) < 60_000) opened = true
    }
  }
  return joined + (opened ? 1 : 0)
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** An open item's headline: "Held until 14:00", "Collecting 3 events until …". */
export function pendingLabel(item: PolicyItem, opts: { count?: number; now?: Date } = {}): Label {
  const when = (iso: string | null | undefined) => formatWhen(iso, opts.now)
  if (item.kind === 'hold') return { label: `Held until ${when(item.release_at)}`, tone: 'held' }
  if (item.kind === 'wait') {
    const wait = item.wait
    if (item.satisfied)
      return {
        label: `Prerequisite arrived; held until ${when(item.hold_until ?? item.release_at)}`,
        tone: 'held',
      }
    return {
      label: wait
        ? `Waiting for ${wait.event} (${wait.until}) until ${when(item.deadline ?? item.release_at)}`
        : `Waiting for a prerequisite until ${when(item.deadline ?? item.release_at)}`,
      tone: 'held',
      detail: wait ? onTimeoutText(wait.on_timeout) : undefined,
    }
  }
  const n = opts.count ?? 0
  return {
    label:
      n > 0
        ? `Collecting ${plural(n, 'event')} until ${when(item.release_at)}`
        : `Collecting until ${when(item.release_at)}`,
    tone: 'held',
    detail: 'Each new event pushes the send back, up to the rule’s maximum wait.',
  }
}

const KIND_NOUN: Record<string, string> = { hold: 'Hold', wait: 'Wait', collect: 'Collection' }

/**
 * Why a held, waiting, or collecting email closed without (or with) sending,
 * from `closed_reason` or a message's `cancelled`/`dropped` detail.
 */
export function closedReasonText(reason: unknown): string | undefined {
  if (!isObj(reason)) return typeof reason === 'string' ? reason : undefined
  const r = typeof reason.reason === 'string' ? reason.reason : undefined
  const by = typeof reason.by === 'string' ? reason.by : undefined
  switch (r) {
    case 'superseded':
      return typeof reason.by_event === 'string'
        ? `Superseded by ${reason.by_event}`
        : 'Superseded by a later event'
    case 'cancelled':
      return by ? `Cancelled by ${by}` : 'Cancelled by a person'
    case 'prerequisite_timeout':
      return 'The prerequisite didn’t arrive in time'
    case 'rule_removed':
      return 'The rule was removed'
    case 'rule_disabled':
      return 'The rule was turned off'
    case 'live_sending_disabled':
      return 'Live sending is off for the application'
    case 'event_type_removed':
      return 'The event type was removed'
    case 'event_type_disabled':
      return 'The event type was turned off'
    case 'empty':
      return 'No events were left to send'
    case 'rule_failed':
      return 'The rule failed for the newest event'
    case 'render_failed':
      return 'The email failed to render'
    case undefined:
      return by ? `Cancelled by ${by}` : undefined
    default:
      return sentence(r.replace(/_/g, ' '))
  }
}

/** A closed item's headline: "Hold released", "Wait dropped: …". */
export function closedLabel(item: PolicyItem): Label {
  const noun = KIND_NOUN[item.kind] ?? 'Pending email'
  const why = closedReasonText(item.closed_reason)
  switch (item.status) {
    case 'released':
      return { label: `${noun} released`, tone: 'info' }
    case 'cancelled':
      return { label: `${noun} cancelled`, tone: 'neutral', detail: why }
    case 'dropped':
      return { label: `${noun} dropped`, tone: 'warning', detail: why }
    default:
      return pendingLabel(item)
  }
}

// ------------------------------------------------------------ the stream

export type TimelineEntry =
  | { kind: 'event'; at: string; id: string; event: EntityEvent; decisions: EventDecisions }
  | { kind: 'message'; at: string; id: string; message: MessageSummary }
  | { kind: 'item'; at: string; id: string; item: PolicyItem }

const ORDER: Record<TimelineEntry['kind'], number> = { event: 0, message: 1, item: 2 }

/**
 * Events (at receipt), messages (at creation), and closed pending items (at
 * closing) in one list, oldest first. At the same instant an event comes
 * before the messages it created. Open items aren't in the stream; the page
 * shows them on their own, first.
 */
export function buildEntries(
  t: Pick<Timeline, 'events' | 'messages' | 'pending'>,
): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    ...t.events.map((e) => ({
      kind: 'event' as const,
      at: e.received_at,
      id: `evt:${e.id}`,
      event: e,
      decisions: parseDecisions(e.decisions),
    })),
    ...t.messages.map((m) => ({
      kind: 'message' as const,
      at: m.created_at,
      id: m.id,
      message: m,
    })),
    ...t.pending
      .filter((p) => p.status !== 'open')
      .map((p) => ({ kind: 'item' as const, at: p.closed_at ?? p.updated_at, id: p.id, item: p })),
  ]
  return entries.sort(
    (a, b) =>
      Date.parse(a.at) - Date.parse(b.at) ||
      ORDER[a.kind] - ORDER[b.kind] ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  )
}

export function openItems(pending: readonly PolicyItem[]): PolicyItem[] {
  return pending
    .filter((p) => p.status === 'open')
    .sort((a, b) => Date.parse(a.release_at) - Date.parse(b.release_at))
}

/**
 * The pending item a hold or wait decision created: the held message from
 * the same request and rule carries the item's `message_id`.
 */
export function itemForDecision(
  event: Pick<EntityEvent, 'request_id'>,
  d: Pick<RuleDecision, 'rule_id' | 'action'>,
  t: Pick<Timeline, 'messages' | 'pending'>,
): PolicyItem | undefined {
  if (d.action !== 'hold' && d.action !== 'wait') return undefined
  if (!event.request_id) return undefined
  const msg = t.messages.find((m) => m.request_id === event.request_id && m.rule_id === d.rule_id)
  return msg ? t.pending.find((p) => p.message_id === msg.id) : undefined
}

export type ModeFilter = 'live' | 'test' | undefined

/** Only live or only test traffic (test and live are separate histories). */
export function filterByMode<T extends Pick<Timeline, 'events' | 'messages' | 'pending'>>(
  t: T,
  mode: ModeFilter,
): T {
  if (!mode) return t
  const keep = (x: { test: boolean }) => x.test === (mode === 'test')
  return {
    ...t,
    events: t.events.filter(keep),
    messages: t.messages.filter(keep),
    pending: t.pending.filter(keep),
  }
}

/** Tallies for the summary line above the stream. */
export function summarize(t: Pick<Timeline, 'events' | 'messages' | 'pending'>) {
  return {
    events: t.events.length,
    messages: t.messages.length,
    open: t.pending.filter((p) => p.status === 'open').length,
    test: t.events.some((e) => e.test) || t.messages.some((m) => m.test),
    live: t.events.some((e) => !e.test) || t.messages.some((m) => !m.test),
  }
}
